import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  Folder,
  FolderFormData,
  FolderFormErrors,
} from '@/constants/types/folder.types';

interface FolderDialogProps {
  open: boolean;
  folder: Folder | null;
  availableFolders: Folder[];
  onClose: () => void;
  onSubmit: (data: FolderFormData) => void;
  isLoading: boolean;
}

export const FolderDialog: React.FC<FolderDialogProps> = ({
  open,
  folder,
  availableFolders,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState<FolderFormData>({
    name: '',
    description: '',
    parentId: null,
  });
  const [errors, setErrors] = useState<FolderFormErrors>({});

  useEffect(() => {
    if (open) {
      if (folder) {
        setFormData({
          name: folder.name,
          description: folder.description,
          parentId: folder.parentId,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          parentId: null,
        });
      }
      setErrors({});
    }
  }, [open, folder]);

  const validateForm = (): boolean => {
    const newErrors: FolderFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Folder name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Folder name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Folder name must be less than 100 characters';
    }

    if (formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange =
    (field: keyof FolderFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleParentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      parentId: value === '' ? null : value,
    }));
    if (errors.parentId) {
      setErrors((prev) => ({
        ...prev,
        parentId: undefined,
      }));
    }
  };

  // Filter out the current folder and its descendants to prevent circular references
  const getAvailableParentFolders = (): Folder[] => {
    if (!folder) return availableFolders;

    // Don't allow selecting itself or any of its potential descendants
    const filtered = availableFolders.filter((f) => f._id !== folder._id);

    // If editing and has a parent, ensure the current parent is in the list
    if (folder.parentId) {
      const currentParent = availableFolders.find((f) => f._id === folder.parentId);
      if (currentParent && !filtered.find((f) => f._id === folder.parentId)) {
        // Add current parent back to the list if it was filtered out
        filtered.push(currentParent);
      }
    }

    return filtered;
  };

  const isEditMode = !!folder;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {isEditMode ? 'Edit Folder' : 'Create New Folder'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isLoading}
          sx={{ color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Folder Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || `${formData.name.length}/100 characters`}
            disabled={isLoading}
            autoFocus
            fullWidth
            required
          />

          <TextField
            select
            label="Parent Folder"
            value={formData.parentId || ''}
            onChange={handleParentChange}
            error={!!errors.parentId}
            helperText={errors.parentId || 'Leave empty to create a root folder (Level 1)'}
            disabled={isLoading}
            fullWidth
            slotProps={{
              select: {
                displayEmpty: true,
              },
            }}
          >
            <MenuItem value="">
              <em>None (Root Folder)</em>
            </MenuItem>
            {getAvailableParentFolders().map((f) => (
              <MenuItem key={f._id} value={f._id}>
                {'  '.repeat(f.level - 1)}
                {f.name} (Level {f.level})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            error={!!errors.description}
            helperText={
              errors.description || `${formData.description.length}/500 characters`
            }
            disabled={isLoading}
            fullWidth
            multiline
            rows={4}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          variant="contained"
          color="primary"
        >
          {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
